import { Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

@Injectable({
	providedIn: 'root'
})
export class ThemeService {
	private readonly modeStorageKey = 'model-theme-mode';
	private readonly legacyModeStorageKey = 'theme';
	private readonly userColorStorageKey = 'model-user-theme-color';
	private readonly defaultPrimaryColor = '#7a0ab1';

	readonly mode = signal<ThemeMode>('light');

	initialize(): void {
		this.applyFromStorage();
	}

	applyFromStorage(): void {
		const storedMode =
			localStorage.getItem(this.modeStorageKey) ??
			localStorage.getItem(this.legacyModeStorageKey);

		const mode: ThemeMode = storedMode === 'dark' ? 'dark' : 'light';

		this.setMode(mode);
		localStorage.removeItem(this.legacyModeStorageKey);

		const userColor = this.getUserColor();

		if (userColor) {
			this.applyPrimary(userColor);
			return;
		}

		this.clearPrimaryOverride();
	}

	setMode(mode: ThemeMode): void {
		this.mode.set(mode);
		localStorage.setItem(this.modeStorageKey, mode);
		localStorage.removeItem(this.legacyModeStorageKey);

		document.documentElement.dataset['theme'] = mode;
		document.documentElement.style.colorScheme = mode;

		requestAnimationFrame(() => {
			this.updateThemeColorMeta(this.getCurrentPrimaryColor());
		});
	}

	toggleMode(): void {
		this.setMode(this.mode() === 'dark' ? 'light' : 'dark');
	}

	applyUserPreference(mode: ThemeMode | null | undefined): void {
		if (!mode) {
			this.applyFromStorage();
			return;
		}

		this.setMode(mode);
	}

	applyPrimary(color: string | null): void {
		const value = (color || '').trim();

		if (!value) {
			this.clearPrimaryOverride();
			return;
		}

		document.documentElement.style.setProperty('--ui-color-primary', value);
		this.updateThemeColorMeta(value);
	}

	rememberUserColor(color: string | null): void {
		const value = (color || '').trim();

		if (!value) {
			this.cleanUserColor();
			return;
		}

		localStorage.setItem(this.userColorStorageKey, value);
		this.applyPrimary(value);
	}

	getUserColor(): string | null {
		const value = localStorage.getItem(this.userColorStorageKey);

		return value?.trim() || null;
	}

	getDefaultPrimaryColor(): string {
		return this.defaultPrimaryColor;
	}

	cleanUserColor(): void {
		localStorage.removeItem(this.userColorStorageKey);
		this.clearPrimaryOverride();
	}

	private clearPrimaryOverride(): void {
		document.documentElement.style.removeProperty('--ui-color-primary');

		requestAnimationFrame(() => {
			this.updateThemeColorMeta(this.getCurrentPrimaryColor());
		});
	}

	private getCurrentPrimaryColor(): string {
		return (
			getComputedStyle(document.documentElement)
				.getPropertyValue('--ui-color-primary')
				.trim() || this.defaultPrimaryColor
		);
	}

	private updateThemeColorMeta(color: string): void {
		let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

		if (!meta) {
			meta = document.createElement('meta');
			meta.name = 'theme-color';
			document.head.appendChild(meta);
		}

		meta.content = color;
	}
}
